import React, { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  FolderIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  EyeIcon,
  PlusIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline'

import { apiClient } from '../../lib/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { useAuth } from '../../hooks/useAuth'
import AutomationStatsWidget from '../../components/automation/AutomationStatsWidget'

const DashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { refetchUser } = useAuth()

  // Clean up OAuth parameters if present (OAuth callback already handled by OAuthCallbackPage)
  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth_success')
    const jwtToken = searchParams.get('jwt_token')
    const provider = searchParams.get('provider')
    const action = searchParams.get('action')

    // If OAuth parameters are present, clean them up from URL
    // No duplicate toast needed - OAuthCallbackPage already showed success message
    if (oauthSuccess || jwtToken || provider || action) {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('oauth_success')
      newParams.delete('jwt_token')
      newParams.delete('provider')
      newParams.delete('action')
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams, setSearchParams])
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['dashboardMetrics'],
    queryFn: () => apiClient.getDashboardMetrics(),
    staleTime: 30000,
  })

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiClient.getProjects(),
    staleTime: 30000,
  })

  const { data: opportunities, isLoading: opportunitiesLoading } = useQuery({
    queryKey: ['recentOpportunities'],
    queryFn: () => apiClient.getOpportunities({ per_page: 5 }),
    staleTime: 30000,
  })

  if (metricsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const stats = [
    {
      name: 'Total Projects',
      value: metrics?.projects?.total || 0,
      icon: FolderIcon,
      // Remove hardcoded changes for now - would need historical data to calculate real changes
      change: null,
      changeType: 'neutral' as const,
    },
    {
      name: 'Active Monitoring',
      value: metrics?.projects?.active || 0,
      icon: EyeIcon,
      change: null,
      changeType: 'neutral' as const,
    },
    {
      name: 'Last 24h Opportunities',
      value: metrics?.opportunities?.new || 0,
      icon: ChatBubbleLeftRightIcon,
      change: null,
      changeType: 'neutral' as const,
    },
    {
      name: 'Conversion Rate',
      value: `${metrics?.performance?.conversion_rate || 0}%`,
      icon: ArrowTrendingUpIcon,
      change: null,
      changeType: 'neutral' as const,
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-gray-200 pb-5">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Welcome back! Here's what's happening with your Reddit lead generation.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow hover:shadow-md transition-shadow"
          >
            <dt>
              <div className="absolute rounded-md bg-blue-500 p-3">
                <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">{stat.name}</p>
            </dt>
            <dd className="ml-16 flex items-baseline">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              {stat.change && (
                <p className={`ml-2 flex items-baseline text-sm font-semibold ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <ArrowTrendingUpIcon className={`h-4 w-4 flex-shrink-0 self-center ${
                    stat.changeType === 'positive' ? 'text-green-500' : 'text-red-500'
                  }`} />
                  <span className="sr-only">{stat.changeType === 'positive' ? 'Increased' : 'Decreased'} by</span>
                  {stat.change}
                </p>
              )}
            </dd>
          </motion.div>
        ))}
      </div>

      {/* Automation Activity Widget */}
      <AutomationStatsWidget />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Projects */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Projects</h3>
              <Link
                to="/projects"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                View all
              </Link>
            </div>
            <div className="mt-6">
              {projectsLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : projects && projects.length > 0 ? (
                <div className="space-y-4">
                  {projects.slice(0, 3).map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{project.name}</p>
                        <p className="text-xs text-gray-500">
                          {(project.subreddits && Array.isArray(project.subreddits)) ? project.subreddits.join(', ') : 'No subreddits'} • {project.status || 'unknown'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {project.metrics?.total_opportunities || 0}
                        </p>
                        <p className="text-xs text-gray-500">opportunities</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No projects</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating your first project.
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/projects"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                      New Project
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Recent Opportunities */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white overflow-hidden shadow rounded-lg"
        >
          <div className="p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Opportunities</h3>
              <Link
                to="/opportunities"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                View all
              </Link>
            </div>
            <div className="mt-6">
              {opportunitiesLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : opportunities?.opportunities && opportunities.opportunities.length > 0 ? (
                <div className="space-y-4">
                  {opportunities.opportunities.map((opportunity) => (
                    <div
                      key={opportunity.id}
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          opportunity.priority === 'high'
                            ? 'bg-red-400'
                            : opportunity.priority === 'medium'
                            ? 'bg-yellow-400'
                            : 'bg-green-400'
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {opportunity.title}
                        </p>
                        <p className="text-xs text-gray-500">
                          r/{opportunity.subreddit} • {opportunity.priority} priority
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No opportunities</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Start monitoring projects to discover opportunities.
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6"
      >
        <div className="text-center">
          <h3 className="text-lg font-medium text-white">Ready to find more leads?</h3>
          <p className="mt-2 text-blue-100">
            Create a new project or explore your existing opportunities.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <Link
              to="/projects"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              New Project
            </Link>
            <Link
              to="/opportunities"
              className="inline-flex items-center px-4 py-2 border border-white text-sm font-medium rounded-md text-white bg-transparent hover:bg-white hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
            >
              <EyeIcon className="-ml-1 mr-2 h-5 w-5" />
              View Opportunities
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default DashboardPage